import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push, replace } from 'connected-react-router';
import moment from 'moment-timezone';
import { debounce } from 'lodash';

import { Button, HEADER_BUTTON_DARK_BLUE } from '../../components/PageHeader/Button';
import Strings from '../../Strings';
import { EndedEnrollmentShippingWarning } from '../../components/EnrollmentShippingWarning';
import Table3, { columnHelper } from '../../containers/Table/Table3';
import { notificationActions } from '../../components/Notification/redux/actions';
import { openModalAction } from '../../actions/modal';
import { GET_ORDERS_RESULT, shippingActions } from './actions';
import { toolTipForTable } from '../../utils';
import { DATE_TIME_SPACE_12_UPPERCASE } from '../../constants';
import { Tabs, Tab } from '../../components/Tabs';
import { ActionButton, ActionWidget } from '../../components/ActionWidget';
import { TextInput } from '../../components/PageHeader/TextInput';
import AdvancedFilters, { Filter } from '../../components/AdvancedFilters/AdvancedFilters';
import { getFiltersDefinition, OrgFilter } from '../../components/AdvancedFilters/FiltersDefinition';
import { getFiltersForRequest } from '../../components/AdvancedFilters/helpers';
import AdvancedFiltersBar from '../../components/AdvancedFilters/AdvancedFiltersBar';
import { hasPermission, PERMISSIONS } from '../../utils/userPermissions';
import { isShipper, isSuperUser } from '../../utils/userRoles';
import { RejectOrderModal } from './OrdersForReview';
import {
  getSearchParamFromUrl,
  hydrateTableRequestFromUrl,
  saveSearchParamToUrl,
  useGlobalPageSize,
} from '../../utils/tableUtils';

export const OrderStatusEnum = {
  pending_review: 'pending_review',
  rejected_by_doctor: 'rejected_by_doctor',
  draft: 'draft',
  open: 'open',
  in_fulfillment: 'in_fulfillment',
  partially_delivered: 'partially_delivered',
  delivered: 'delivered',
  cancelled: 'cancelled',
}

export const OrderTypeEnum = {
  standard: 'standard',
  sample: 'sample',
  replacement: 'replacement',
}

export const ShipmentMovementScopeEnum = {
  full_kit: 'full_kit',
    partial_devices: 'partial_kit',
};

export const ReturnMethodEnum = {
  mail: 'mail',
  pharmacy_walk_in: 'pharmacy_walk_in',
  bulk_transfer: 'bulk_transfer',
};

export const SmartmeterShippingMethodEnum = {
  MAIL: 'MAIL',
  MAIL_SIG: 'MAIL_SIG',
  UP2: 'UP2',
  UP2_SIG: 'UP2_SIG',
};

export const formatDevices = devices => {
    if (!devices || devices.length === 0) return '-';
    const list = devices.map(d => `${d.device_type} ${d.device_manufacturer} ${d.device_model} (${d.quantity})`);
    return toolTipForTable(list.join(', '), list);
  };

export const tabs = [
  {id: 'Pending Review', label: 'Pending Review', states: [OrderStatusEnum.pending_review], permission: PERMISSIONS.SHIPPING_PENDING_REVIEW_ORDERS_VIEW},
  {id: 'Rejected by Doctor', label: 'Rejected', states: [OrderStatusEnum.rejected_by_doctor], permission: PERMISSIONS.SHIPPING_PENDING_REVIEW_ORDERS_VIEW},
  {id: 'Drafts', label: 'Drafts', states: [OrderStatusEnum.draft], permission: PERMISSIONS.SHIPPING_ORDERS_VIEW},
  {id: 'Open', label: 'Open', states: [OrderStatusEnum.open], permission: PERMISSIONS.SHIPPING_ORDERS_VIEW},
  {id: 'In Fullfilment', label: 'In Fullfilment', states: [OrderStatusEnum.in_fulfillment, OrderStatusEnum.partially_delivered], permission: PERMISSIONS.SHIPPING_ORDERS_VIEW},
  {id: 'Completed', label: 'Completed', states: [OrderStatusEnum.delivered], permission: PERMISSIONS.SHIPPING_ORDERS_VIEW},
  {id: 'Cancelled', label: 'Cancelled', states: [OrderStatusEnum.cancelled], permission: PERMISSIONS.SHIPPING_ORDERS_VIEW}
];

export const REJECTED_ADDRESS_2 = 'SmartMeter rejected the shipping address: Street name, city, state, and ZIP are all valid. Entire address is valid. The submitted secondary information (apartment, suite, etc.) was not recognized. Secondary number is NOT REQUIRED for delivery.';

// Preview blockers a shipper may explicitly acknowledge; other blocker codes
// keep the submit disabled until the underlying condition is fixed. Thunks so
// the text follows runtime language switches.
export const SMARTMETER_ACK_BLOCKERS = {
  sku_without_device: () => Strings.shipping.smartmeterAckSkuWithoutDevice,
  sku_model_mismatch: () => Strings.shipping.smartmeterAckSkuModelMismatch,
  rejected_address_2: () => Strings.shipping.smartmeterAddress2Rejected,
};

const renderOrderIdCell = info => (
  <div className="icon-and-text-container">
    <div className="icon-and-text-content">
      <div className="clickable">{info.getValue()}</div>
    </div>
    <EndedEnrollmentShippingWarning order={info.row.original} compact />
  </div>
);

function ShippingOrders(props) {
  const { refreshTimestamp, filters, orgs, getOrders } = props;
  const { openModal, onNavigate, replacePath, openConfirmModal, refreshData, rejectOrder, showNotification  } = props;
  const globalPageSize = useGlobalPageSize();
  const urlParams = new URLSearchParams(window.location.search);
  const statusFromUrl = urlParams.get('status');

  const [status, setStatus] = useState(statusFromUrl || tabs[3].id);
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({ offset: 0, limit: globalPageSize, totalRecords: 0 });
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [searchText, setSearchText] = useState(getSearchParamFromUrl() || '');
  
  const tableRef = useRef(null);
  const pageRequest = useRef({ offset: 0, limit: globalPageSize, search: getSearchParamFromUrl() });
  const requestSequence = useRef(0);
  const hasLoadedOnce = useRef(false);

  const loadData = React.useCallback(async () => {
    requestSequence.current += 1;
    const sequence = requestSequence.current;
    setLoading(true);
    try {
      const resp = await getOrders(pageRequest.current);
      if (sequence !== requestSequence.current) return;
      if (resp?.type === GET_ORDERS_RESULT) {
        setOrders(resp.response?.data || []);
        setPagination(resp.response?.pagination || { ...pageRequest.current, totalRecords: 0 });
        setStatusCounts(resp.response?.status_counts || {});
        tableRef.current?.resetSelectedRows();
      }
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [getOrders]);

  const onPrepareShipmentsSuccess = React.useCallback(() => {
    tableRef.current?.resetSelectedRows();
    refreshData();
  }, [refreshData]);
  const canEdit = hasPermission(PERMISSIONS.SHIPPING_ORDERS_EDIT) || isShipper() || isSuperUser();
  const canReview = hasPermission(PERMISSIONS.SHIPPING_SMART_ORDERING_EDIT) || isShipper() || isSuperUser();

  useEffect(() => {
    if (status) {
      const newUrlParams = new URLSearchParams(window.location.search);
      newUrlParams.set('status', status);
      replacePath(`${window.location.pathname}?${newUrlParams}`);
    }
  }, [status]);

  React.useEffect(() => {
    const selectedTab = tabs.find(tab => tab.id === status) || tabs[3];
    const relevantGroups = ['order_category', 'target_org_uuid', 'billed_to_org_uuid', 'flow', 'fulfillment_provider'];
    const filterBy = getFiltersForRequest(filters.filter(filter => relevantGroups.includes(filter.group)));
    filterBy.push(`status:${selectedTab.states.join(',')}`);

    if (!hasLoadedOnce.current) {
      hydrateTableRequestFromUrl(pageRequest.current, globalPageSize);
      pageRequest.current = { ...pageRequest.current, filterBy };
      hasLoadedOnce.current = true;
    } else {
      pageRequest.current = { ...pageRequest.current, offset: 0, filterBy };
    }

    loadData();
  }, [filters, globalPageSize, loadData, refreshTimestamp, status]);

  const displayedOrders = React.useMemo(() => orders.map(order => ({
    ...order,
    org_name: orgs.find(org => org.masked_id === order.org_uuid)?.name,
    target_org_name: orgs.find(org => org.masked_id === order.target_org_uuid)?.name,
    billed_org_name: orgs.find(org => org.masked_id === order.billed_to_org_uuid)?.name,
  })), [orders, orgs]);

  const onSearchQueryChange = React.useMemo(() => debounce(search => {
    pageRequest.current = { ...pageRequest.current, offset: 0, search };
    saveSearchParamToUrl(search);
    loadData();
  }, 300), [loadData]);

  React.useEffect(() => () => onSearchQueryChange.cancel(), [onSearchQueryChange]);

  const onOffsetChange = offset => {
    pageRequest.current = { ...pageRequest.current, offset };
    loadData();
  };

  const onPageSizeChange = limit => {
    pageRequest.current = { ...pageRequest.current, offset: 0, limit };
    loadData();
  };

  const onSortingChange = sorting => {
    const next = sorting?.[0];
    const sortColumn = next?.id;
    const sortType = next ? (next.desc ? 'descending' : 'ascending') : undefined;
    if (pageRequest.current.sortColumn === sortColumn && pageRequest.current.sortType === sortType) return;
    pageRequest.current = { ...pageRequest.current, offset: 0, sortColumn, sortType };
    loadData();
  };

  const onDeleteOrder = order => {
    openConfirmModal({
      title: Strings.formatString(
        Strings.shipping.deleteOrderQuestion,
        order.order_uuid,
      ),
      onConfirmAction: shippingActions.deleteOrder(order.order_uuid),
      caption: Strings.shipping.deleteOrder,
      hideCaution: true,
      confirmPostAction: refreshData,
    });
  };

  const onCancel = order => {
    openConfirmModal({
      title: Strings.formatString(
        Strings.shipping.cancelOrderQuestion,
        order.order_uuid,
      ),
      onConfirmAction: shippingActions.editOrder(order.order_uuid, { status: OrderStatusEnum.cancelled }),
      caption: Strings.shipping.cancelOrder,
      hideCaution: true,
      confirmPostAction: refreshData,
    });
  };

  const onPlaceOrder = order => {
    openConfirmModal({
      title: Strings.formatString(
        Strings.shipping.placeOrderQuestion,
        order.order_uuid,
      ),
      onConfirmAction: shippingActions.editOrder(order.order_uuid, { status: OrderStatusEnum.open }),
      caption: Strings.shipping.placeOrder,
      hideCaution: true,
      confirmPostAction: refreshData,
    });
  };

  const onAcceptOrder = order => {
    openConfirmModal({
      title: Strings.formatString(
        Strings.shipping.acceptOrderQuestion,
        order.patient_name,
      ),
      onConfirmAction: shippingActions.acceptOrder(order.order_uuid, {}),
      onSuccessNotification: Strings.shipping.orderAcceptedSuccessfully,
      caption: Strings.shipping.acceptOrder,
      hideCaution: true,
      confirmPostAction: refreshData,
    });
  };

  const onRejectOrder = (order, reason) => {
    rejectOrder(order.order_uuid, reason).then(() => {
      refreshData();
      setSelectedOrder(null);
      setShowRejectModal(false);
      showNotification(Strings.shipping.orderRejectedSuccessfully);
    });
  }

  const buttons = React.useMemo(() => [
    {
      icon: 'package',
      onClick: (_, order) => openModal('prepare-shipments', { ordersIds: [order.order_uuid], onSuccess: refreshData, orgs }),
      text: (_, order) => order.status === OrderStatusEnum.open ? Strings.shipping.prepareShipment : Strings.shipping.prepareAnotherShipment,
      visible: (_, order) => 
        [OrderStatusEnum.open, OrderStatusEnum.in_fulfillment, OrderStatusEnum.partially_delivered].includes(order.status)
        && hasPermission(PERMISSIONS.SHIPPING_SHIPMENTS_EDIT),
      disabledTooltip: Strings.shipping.prepareShipmentDisabledTooltip,
    },
    {
      icon: 'register',
      onClick: (_, order) => openModal('place-smartmeter-order-modal', { order, onSuccess: refreshData }),
      text: Strings.shipping.placeSmartmeterOrder,
      visible: (_, order) => 
        [OrderStatusEnum.open, OrderStatusEnum.in_fulfillment, OrderStatusEnum.partially_delivered].includes(order.status)
        && order.order_category === 'direct'
        && order.order_items?.some(item => item.device_manufacturer === 'smartmeter')
        && hasPermission(PERMISSIONS.SHIPPING_SHIPMENTS_EDIT),
      disabled: (_, order) => order.has_smartmeter_fulfillment,
      disabledTooltip: Strings.shipping.smartmeterOrderAlreadyPlaced,
    },
    {
      icon: 'check',
      onClick: (_, order) => onAcceptOrder(order),
      text: Strings.shipping.acceptOrder,
      visible: (_, order) => order.status === OrderStatusEnum.pending_review && canReview,
    },
    {
      icon: 'edit',
      onClick: (_, order) => onNavigate(`/shipping/order-details/${order.order_uuid}`),
      text: Strings.edit,
    },
    {
      icon: 'shipping',
      onClick: (_, order) => onPlaceOrder(order),
      text: Strings.shipping.placeOrder,
      visible: (_, order) => order.status === OrderStatusEnum.draft,
    },
    {
      icon: 'cancelEnrollment',
      onClick: (_, order) => onCancel(order),
      text: Strings.shipping.cancelOrder,
      visible: (_, order) => ![OrderStatusEnum.cancelled, OrderStatusEnum.delivered, OrderStatusEnum.pending_review].includes(order.status),
    },
    {
      icon: 'delete',
      onClick: (_, order) => onDeleteOrder(order),
      text: Strings.delete,
      visible: (_, order) => order.status === OrderStatusEnum.draft,
    },
    {
      icon: 'delete',
      onClick: (_, order) => {setSelectedOrder(order); setShowRejectModal(true);},
      text: Strings.shipping.rejectOrder,
      visible: (_, order) => order.status === OrderStatusEnum.pending_review && canReview,
    },
  ]);

  const columns = React.useMemo(() => [
    columnHelper.accessor('order_uuid', {
      header: Strings.shipping.orderId,
      cell: renderOrderIdCell,
      size: 250,
    }),
    columnHelper.accessor('flow', {
      header: Strings.shipping.shippingFlow,
      cell: info => info.getValue() ? (Strings.shipping.shippingFlows[info.getValue()] || info.getValue()) : '-',
      size: 200,
    }),
    columnHelper.accessor('target_org_name', {
      header: Strings.shipping.targetOrganization,
    }),
    columnHelper.accessor('billed_org_name', {
      header: Strings.shipping.billedOrganization,
    }),
    columnHelper.accessor('patient_name', {
      header: Strings.patientNameText,
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('pharmacy_patient_id', {
      header: Strings.capPatient.patientId,
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('order_items', {
      header: Strings.devices,
      cell: info => formatDevices(info.getValue()),
      size: 300,
      enableSorting: false,
    }),
    columnHelper.accessor('status', {
      header: Strings.status,
      cell: info => info.getValue() ? (Strings.shipping.orderStatuses[info.getValue()] || info.getValue()) : '-',
      size: 150,
      enableSorting: true,
    }),
    columnHelper.accessor('order_type', {
      header: Strings.shipping.orderType,
      cell: info => Strings.shipping.orderTypes[info.getValue()] || info.getValue(),
      size: 150,
    }),
    columnHelper.accessor('order_category', {
      header: Strings.shipping.orderCategory,
      cell: info => Strings.shipping.orderCategories[info.getValue()] || info.getValue(),
      size: 150,
    }),
    columnHelper.accessor('shipping_method', {
      header: Strings.shipping.shippingMethod,
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('created_at', {
      header: Strings.createdAt,
      cell: info => info.getValue() ? moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE) : '-',
      size: 150,
    }),
    columnHelper.accessor('updated_at', {
      header: Strings.updatedAt,
      cell: info => info.getValue() ? moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE) : '-',
      size: 150,
    }),
    columnHelper.accessor('created_by_name', {
      header: Strings.createdBy,
      cell: info => info.getValue() || '-',
      size: 150,
    }),
    columnHelper.accessor('updated_by_name', {
      header: Strings.updatedBy,
      cell: info => info.getValue() || '-',
      size: 150,
    }),
  ], []);

  const getTabRightContent = React.useCallback(() => {
    return (
      <>
        {canEdit && (
          <Button
            class={HEADER_BUTTON_DARK_BLUE}
            onClick={() => onNavigate('/shipping/order-details/new')}
            title={Strings.createOrder}
          />
        )}
        <TextInput
          placeholder={Strings.search}
          value={searchText}
          onChange={e => {
            setSearchText(e.target.value);
            onSearchQueryChange(e.target.value);
          }}
        />
        <AdvancedFilters>
          <Filter definition={getFiltersDefinition().flow} />
          <Filter definition={getFiltersDefinition().order_category} />

          {orgs?.length > 0 && <Filter definition={OrgFilter(orgs, false, 'target_org_uuid', Strings.shipping.targetOrganization).target_org_uuid} />}
          {orgs?.length > 0 && <Filter definition={OrgFilter(orgs, false, 'billed_to_org_uuid', Strings.shipping.billedOrganization).billed_to_org_uuid} />}
          <Filter definition={getFiltersDefinition().fulfillment_provider} />
        </AdvancedFilters>
      </>
    );
  }, [status, orgs]);

  const permittedTabs = React.useMemo(() => tabs.filter(tab => hasPermission(tab.permission)), []);
  const tabArray = React.useMemo(() => permittedTabs.map(s => (
    <Tab label={s.label} key={s.id} count={s.states.reduce((total, state) => total + (statusCounts[state] || 0), 0)}>
      <Table3
        isLoading={loading}
        name="shipping-orders"
        data={displayedOrders}
        columns={columns}
        onRowClick={(order) => onNavigate(`/shipping/order-details/${order.order_uuid}`)}
        onRowSelectionChange={s.id === tabs[3].id ? setSelectedOrders : undefined}
        enableColumnFiltering
        buttons={buttons}
        noScroll
        pinFirstColumn
        ref={tableRef}
        pagination={pagination}
        onOffsetChange={onOffsetChange}
        onPageSizeChange={onPageSizeChange}
        onSortingChange={onSortingChange}
      />
      <ActionWidget show={selectedOrders?.length > 0}>
        <ActionButton
          img="action-package"
          tooltiptext={Strings.shipping.prepareShipments}
          text={Strings.shipping.prepareShipments}
          action={() => openModal('prepare-shipments', { ordersIds: selectedOrders?.map(order => order.order_uuid), onSuccess: onPrepareShipmentsSuccess, orgs })}
        />
      </ActionWidget>
    </Tab>
  )), [loading, columns, buttons, selectedOrders, displayedOrders, onPrepareShipmentsSuccess, pagination, permittedTabs, statusCounts]);

  return (
    <>
      <Tabs
        containerClassName="shipping-list-tabs"
        defaultIndex={tabArray.findIndex(t => t.key === status)}
        onChange={i => setStatus(permittedTabs[i].id)}
        rightContent={getTabRightContent()}
        contentHeader={(
          <AdvancedFiltersBar
            customFilters={{...OrgFilter(orgs, false, 'target_org_uuid', Strings.shipping.targetOrganization), ...OrgFilter(orgs, false, 'billed_to_org_uuid', Strings.shipping.billedOrganization)}}
          />
        )}
      >
        {tabArray}
      </Tabs>
      {showRejectModal && (
        <RejectOrderModal
          onCancel={() => {setShowRejectModal(false); setSelectedOrder(null);}}
          onConfirm={(reason) => onRejectOrder(selectedOrder, reason)}
        />
      )}
    </>
  );
}

ShippingOrders.propTypes = {
  deleteOrder: PropTypes.func.isRequired,
  refreshTimestamp: PropTypes.any,
  showNotification: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  filters: PropTypes.array,
  orgIds: PropTypes.string,
  orgs: PropTypes.array,
  onNavigate: PropTypes.func.isRequired,
  replacePath: PropTypes.func.isRequired,
  refreshData: PropTypes.func.isRequired,
  rejectOrder: PropTypes.func.isRequired,
  getOrders: PropTypes.func.isRequired,
  openConfirmModal: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  filters: state.entities.advancedFilters.filters.items,
  orgIds: state.entities?.orgFilter?.orgIds,
});

const mapDispatchToProps = dispatch => ({
  deleteOrder: id => dispatch(shippingActions.deleteOrder(id)),
  rejectOrder: (order, reason) => dispatch(shippingActions.rejectOrder(order, {reason})),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
  onNavigate: url => dispatch(push(url)),
  replacePath: url => dispatch(replace(url)),
  openModal: (name, data) => dispatch(openModalAction(name, data)),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
  getOrders: pageRequest => dispatch(shippingActions.getOrders(pageRequest)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ShippingOrders);
